import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import Hero from './components/Hero';
import CategoryGridA from './components/CategoryGridA';
import CategoryGridB from './components/CategoryGridB';
import AboutSection from './components/AboutSection';
import ArticleCards from './components/ArticleCards';
import CTASection from './components/CTASection';
import TestimonialSection from './components/TestimonialSection';

const siteUrl = typeof __SITE_URL__ !== 'undefined' ? __SITE_URL__ : 'https://itaobuyspread.bond';

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      name: 'Itaobuy Spreadsheet Italia',
      url: siteUrl,
      description: 'Guida italiana completa a Itaobuy Spreadsheet con liste prodotti aggiornate, 14 categorie, recensioni e consigli per acquisti sicuri.',
      inLanguage: 'it',
      publisher: {
        '@type': 'Organization',
        name: 'Itaobuy Spreadsheet',
        url: siteUrl,
      },
    },
    {
      '@type': 'Organization',
      name: 'Itaobuy Spreadsheet',
      url: siteUrl,
      description: 'Portale italiano dedicato a Itaobuy Spreadsheet con guide, categorie prodotti e informazioni per acquisti online.',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl,
        },
      ],
    },
  ],
};

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={homeJsonLd as unknown as Record<string, unknown>} />
      <Navbar />

      <main>
        <Hero />

        <section className="w-full bg-background-50 py-12 md:py-16 px-4 md:px-8">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              <div className="rounded-2xl bg-background-100 overflow-hidden" style={{ animation: 'fadeInUp 0.7s ease-out 0.1s forwards', opacity: 0 }}>
                <div className="h-36 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Organized%20product%20shelves%20with%20labeled%20boxes%20and%20neatly%20arranged%20fashion%20items%2C%20warm%20wooden%20shelving%2C%20soft%20warehouse%20lighting%2C%20systematic%20organization%20aesthetic%2C%20clean%20and%20structured%20product%20photography&width=600&height=400&seq=benefit-organized&orientation=landscape"
                    alt="Prodotti Organizzati"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-layout-grid-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Prodotti Organizzati</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">Trova rapidamente categorie e articoli popolari con liste sempre aggiornate.</p>
                </div>
              </div>
              <div className="rounded-2xl bg-background-100 overflow-hidden" style={{ animation: 'fadeInUp 0.7s ease-out 0.2s forwards', opacity: 0 }}>
                <div className="h-36 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20detailed%20product%20specification%20cards%20with%20close%20up%20view%20of%20fabric%20textures%20and%20labels%2C%20analytical%20inspection%20concept%2C%20warm%20desk%20lighting%2C%20detailed%20quality%20assessment%20photography&width=600&height=400&seq=benefit-detail&orientation=landscape"
                    alt="Informazioni Dettagliate"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-file-search-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Informazioni Dettagliate</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">Analizza caratteristiche, disponibilità e prezzi prima di acquistare.</p>
                </div>
              </div>
              <div className="rounded-2xl bg-background-100 overflow-hidden" style={{ animation: 'fadeInUp 0.7s ease-out 0.3s forwards', opacity: 0 }}>
                <div className="h-36 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Hourglass%20with%20fashion%20items%20blurring%20past%20in%20time%20lapse%20effect%2C%20time%20saving%20concept%2C%20warm%20golden%20light%2C%20efficiency%20and%20speed%20theme%2C%20creative%20conceptual%20product%20photography%20with%20artistic%20motion%20blur&width=600&height=400&seq=benefit-time&orientation=landscape"
                    alt="Risparmio Tempo"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-timer-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Risparmio Tempo</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">Evita ricerche manuali complesse e trova subito ciò che ti serve.</p>
                </div>
              </div>
              <div className="rounded-2xl bg-background-100 overflow-hidden" style={{ animation: 'fadeInUp 0.7s ease-out 0.4s forwards', opacity: 0 }}>
                <div className="h-36 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Protective%20shield%20symbol%20made%20of%20translucent%20material%20hovering%20over%20shopping%20bags%20and%20a%20credit%20card%2C%20safety%20and%20security%20concept%2C%20soft%20blue%20ambient%20glow%2C%20trust%20and%20protection%20theme%2C%20clean%20conceptual%20product%20photography&width=600&height=400&seq=benefit-security&orientation=landscape"
                    alt="Maggiore Sicurezza"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-shield-check-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Maggiore Sicurezza</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">Segui guide e consigli verificati prima di ogni acquisto.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CategoryGridA />
        <CategoryGridB />
        <AboutSection />
        <ArticleCards />
        <CTASection />
        <TestimonialSection />
      </main>

      <Footer />
    </div>
  );
}